/*
 * Kb Audit Search Store
 * */
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useDateRangeStore } from '@/store/DaterangeStore';
import { useAuditFiltersStore } from '@/store/KbAuditFiltersStore';
import { AnalyticsQuery } from '@/analytics/AnalyticsQuery';
import { useAnalyticsStore } from '@/store/AnalyticsStore';

/*
 * Kb Audit Search Store
 * */
export const useKbAuditSearchStore = defineStore('kbAuditSearch', () => {
  const dateRangeStore = useDateRangeStore();
  const filtersStore = useAuditFiltersStore();
  const analyticsStore = useAnalyticsStore();

  // Search parameters
  const searchQuery = ref('*');
  const searchSize = ref(50);
  const searchOffset = ref(0);
  const sortFields = ref(null);
  const sortFieldName = ref('datetime');
  const searchTimeIntervalTs = ref(0);

  // Search results
  const auditRecords = ref(null);
  const auditRecordsTotal = ref(0);
  const auditCache = {};

  // Add / update audit records to cache
  // When searched and found some records, we can add them to cache
  // so that we don't need to reload records from ES when opening details view
  function cacheAuditRecords(records) {
    if (!Array.isArray(records) || records.length <= 0) {
      return;
    }
    for (let c of records) {
      const id = c?.id || null;
      if (id) {
        auditCache[id] = c;
      }
    }
  }

  // Sort: [{field:'name',type:'asc|desc'},...]
  async function sort(sort) {
    sortFields.value = sort;
    sortFieldName.value = Array.isArray(sort) && sort.length > 0 ? sort[0]?.field || 'datetime' : 'datetime';
    searchOffset.value = 0;
    return executeSearch({ dateHistogram: false });
  }

  async function page(offset, size) {
    searchSize.value = size;
    searchOffset.value = offset;
    return executeSearch({ dateHistogram: false });
  }

  async function search(query = '*', size = 10, offset = 0, sort = null, dateHistogram = true, filters = null) {
    searchQuery.value = query === '' ? '*' : query;
    searchSize.value = size;
    searchOffset.value = offset;
    sortFields.value = sort;
    return executeSearch({ dateHistogram: dateHistogram });
  }

  async function executeSearch({ dateHistogram = true }) {
    const filters = filtersStore.toElasticFilters();
    searchTimeIntervalTs.value = dateRangeStore.timeIntervalChanged;
    const q = AnalyticsQuery.searchQuery(dateRangeStore.timeInterval, searchQuery.value, searchSize.value, searchOffset.value, sortFields.value, dateHistogram, filters, 'audit');
    await analyticsStore.executeAnalyticsQuery(q);
    if (q.success) {
      const auditDocs = (q?.result?.hits || []).map((x) => x._source);
      auditRecords.value = Object.freeze(auditDocs); //markRaw(conversationsDocs);
      auditRecordsTotal.value = q?.result?.total || 0;
      cacheAuditRecords(auditDocs);
    }
  }

  return {
    search,
    sort,
    page,
    cacheAuditRecords: cacheAuditRecords,
    auditRecords: auditRecords,
    auditRecordsTotal: auditRecordsTotal,
    searchQuery,
    searchSize,
    searchOffset,
    searchTimeIntervalTs,
    sortFields,
    sortFieldName,
  };
});
